import React from "react";
import Table from "../Table";
import { useHistory } from "react-router";
import { Tag } from "antd";
import { get, isEmpty, startCase } from "lodash-es";
import { getAppointmentColor } from "../../../../../helperFunctions/_common/getColor";
import moment from "moment";
import { Fulfillment } from "@secondcloset/types";
import { fetchShipmentIndex, ShipmentIndexQuery } from "../../../../../api/logistics/shipment";
import { useQuery } from "react-query";
type Shipment = Fulfillment.Shipment;

interface ShipmentsTableProps {
  query: ShipmentIndexQuery;
}

const ShipmentsTable: React.FC<ShipmentsTableProps> = ({ query }) => {
  const history = useHistory();
  const organizationID = query.organization_id;

  const shipmentIndex = useQuery(["shipmentIndex", query], () => fetchShipmentIndex(query), {
    enabled: !isEmpty(query),
  });

  const getShipmentsDataSource = (data: Shipment[]) => {
    if (isEmpty(data)) return [];
    return data.map((shipment) => {
      return {
        ...shipment,
        customerName: get(shipment.shipping_method, "source.customer.name"),
      };
    });
  };

  const getShipmentsColumns = () => {
    const renderOrderID = (orderID: string) => {
      const last4 = orderID?.slice(-4);
      return (
        <div
          key={orderID}
          className="column-text clickable"
          onClick={() => history.push(`/fulfillment/orders/${orderID}`)}
        >
          {last4}
        </div>
      );
    };

    const renderShipmentStatus = (_: unknown, record: Shipment) => {
      const tag = get(record, "shipping_method.status", []);
      const tagText = startCase(tag);
      const color = getAppointmentColor("status", tag);
      return (
        <Tag key={tagText + record.id} color={color}>
          {tagText}
        </Tag>
      );
    };

    const renderShipmentType = (text: string, record: Shipment) => {
      const tags = record.action_summary;
      return tags.map((tag, index) => {
        if (tag === "setup") return "";
        const color = getAppointmentColor("type", tag);
        const tagText = startCase(tag);
        return (
          <Tag key={tagText + record.id + index} color={color}>
            {tagText}
          </Tag>
        );
      });
    };

    const renderCarrier = (text: string, record: Shipment) => {
      const carrierCode = get(record, "carrier.carrier_code");
      return (
        <div key={text + record.id} className="column-text">
          {startCase(carrierCode)}
        </div>
      );
    };

    const renderShipmentDate = (_: unknown, record: Shipment) => {
      const date = get(record, "shipping_method.date");
      const formattedDate = date ? moment.utc(date).format("ll") : "-";
      return (
        <div key={date + record.id} className="column-text">
          {formattedDate}
        </div>
      );
    };

    return [
      {
        title: "Order ID",
        dataIndex: "external_order_id",
        key: "external_order_id",
        render: renderOrderID,
        width: 120,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: renderShipmentStatus,
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: renderShipmentType,
      },
      {
        title: "Customer",
        dataIndex: "customerName",
        key: "customerName",
      },
      {
        title: "Carrier",
        dataIndex: "carrier",
        key: "carrier",
        render: renderCarrier,
      },
      {
        dataIndex: "date",
        key: "date",
        render: renderShipmentDate,
      },
    ];
  };

  const tableProps = {
    columns: getShipmentsColumns(),
    dataSource: getShipmentsDataSource(shipmentIndex.data as unknown as Shipment[]),
    loading: shipmentIndex.isLoading,
  };

  return (
    <div className="fulfillment-table">
      <Table
        headerButtons={[]}
        tableProps={tableProps}
        title="Shipments"
        fullListLink={organizationID ? `/organizations/${organizationID}/shipments` : null}
      />
    </div>
  );
};

export default ShipmentsTable;
